import React, { useEffect } from "react"
import { BackIcon, Button, DialogContent, DialogTitle, ForwardIcon, Stepper } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { DialogActionsBar, Form, FormOnSubmitHandler } from "@carrotfertility/carotene-core-x"
import { CarrotMatchGeneralCloseButton } from "#/components/carrot-match-fertility-clinic-precision-matching/steps/CarrotMatchGeneralCloseButton"
import { useCarrotMatch } from "#/components/carrot-match-fertility-clinic-precision-matching/context/CarrotMatchContext"
import {
  SpecialServices,
  User,
  NONE_OF_THESE,
  Questions
} from "#/components/carrot-match-fertility-clinic-precision-matching/utils/carrotMatchTypes"
import { FormCheckboxButtonGroup } from "#/services/common-forms/FormCheckboxButtonGroup"
import { useHandleCheckboxGroupNoneOfThese } from "#/components/carrot-match-fertility-clinic-precision-matching/hooks/useHandleCheckboxGroupNoneOfThese"

type WhatSpecialServicesCheckboxOption = { label: string; value: SpecialServices | typeof NONE_OF_THESE }
function WhatSpecialServicesCheckboxGroup({ name }: { name: string }): JSX.Element {
  const intl = useIntl()
  const handleCheckboxGroup = useHandleCheckboxGroupNoneOfThese(name)

  const options: WhatSpecialServicesCheckboxOption[] = [
    {
      label: intl.formatMessage({
        defaultMessage: "Remote monitoring"
      }),
      value: SpecialServices.REMOTE_MONITORING
    },
    {
      label: intl.formatMessage({ defaultMessage: "Onsite genetic counseling" }),
      value: SpecialServices.ONSITE_GENETIC_COUNSELING
    },
    {
      label: intl.formatMessage({ defaultMessage: "Mental health services" }),
      value: SpecialServices.MENTAL_HEALTH_SERVICES
    },
    {
      label: intl.formatMessage({ defaultMessage: "Onsite dietitian" }),
      value: SpecialServices.ONSITE_DIETITIAN
    },
    {
      label: intl.formatMessage({ defaultMessage: "Acupuncture" }),
      value: SpecialServices.ACUPUNCTURE
    },
    {
      label: intl.formatMessage({ defaultMessage: "None of these" }),
      value: NONE_OF_THESE
    }
  ]

  return <FormCheckboxButtonGroup name={name} options={options} onChange={handleCheckboxGroup} />
}

export function WhatSpecialServicesXYZLookingForStep() {
  const { send, back } = useStateMachine()
  const { carrotMatchInteraction, updateCarrotMatchInteraction } = useCarrotMatch()
  const {
    memberPreferredNameOrFirstName,
    partnerPreferredNameOrFirstName,
    whoIsAnsweringTheIntake,
    whoIsSeekingCare,
    questionsAndAnswers
  } = carrotMatchInteraction
  const intl = useIntl()

  useEffect(() => {
    updateCarrotMatchInteraction({ lastQuestionViewedBeforeLeavingFlow: Questions.SPECIAL_SERVICES_QUESTION })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const getWhatSpecialServicesDialogTitle = () => {
    if (whoIsAnsweringTheIntake === whoIsSeekingCare) {
      return intl.formatMessage({ defaultMessage: "What special services are you looking for?" })
    } else if (whoIsSeekingCare === User.PARTNER) {
      return intl.formatMessage(
        { defaultMessage: "What special services is {partnerPreferredNameOrFirstName} looking for?" },
        { partnerPreferredNameOrFirstName }
      )
    } else {
      return intl.formatMessage(
        { defaultMessage: "What special services is {memberPreferredNameOrFirstName} looking for?" },
        { memberPreferredNameOrFirstName }
      )
    }
  }

  const handleSubmitAndProceedToNextStep: FormOnSubmitHandler<{
    specialServices: SpecialServices[]
  }> = ({ specialServices }: { specialServices: SpecialServices[] }) => {
    updateCarrotMatchInteraction({
      questionsAndAnswers: [
        ...questionsAndAnswers.filter((qa) => qa.question !== Questions.SPECIAL_SERVICES_QUESTION),
        {
          question: Questions.SPECIAL_SERVICES_QUESTION,
          answer: specialServices
        }
      ]
    })
    send("")
  }

  const handleSkipAndProceedToNextStep = () => {
    updateCarrotMatchInteraction({
      questionsAndAnswers: [
        ...questionsAndAnswers.filter((qa) => qa.question !== Questions.SPECIAL_SERVICES_QUESTION),
        {
          question: Questions.SPECIAL_SERVICES_QUESTION,
          answer: []
        }
      ]
    })
    send("")
  }

  return (
    <>
      <Form onSubmit={handleSubmitAndProceedToNextStep}>
        <CarrotMatchGeneralCloseButton />
        <Stepper variant="progress" steps={4} activeStep={1} position="static" />
        <DialogTitle id="step-heading">{getWhatSpecialServicesDialogTitle()}</DialogTitle>
        <DialogContent>
          <WhatSpecialServicesCheckboxGroup name="specialServices" />
        </DialogContent>
        <DialogActionsBar
          primaryAction={
            <Button variant="outlined" color="secondary" endIcon={<ForwardIcon />} type="submit">
              <FormattedMessage defaultMessage="Continue" />
            </Button>
          }
          secondaryAction={
            <Button variant="text" color="secondary" type="submit" onClick={handleSkipAndProceedToNextStep}>
              <FormattedMessage defaultMessage="Skip" />
            </Button>
          }
          tertiaryAction={
            <Button onClick={() => back()} variant="text" color="secondary" startIcon={<BackIcon />}>
              <FormattedMessage defaultMessage="Back" />
            </Button>
          }
        />
      </Form>
    </>
  )
}
